import { StatusCodes } from "http-status-codes"
import { useCallback, useMemo } from "react"
import { toast } from "react-toastify"
import { useDeleteConfirm } from "../../../../../../hooks/useDeleteConfirm"
import { digitalManagerApi } from "../../../../../../services/api"
import { ApplicationPermissionUserGet } from "../../../../../models/ApplicationPermissionUser"
import { ApplicationPropertyDetail } from "../../../../../models/ApplicationProperty"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToApplicationFeature,
} from "../../../../../models/Authorization"
import { UserInfo } from "../../../../../models/User"
import { DiMaAddButton } from "../../../../partials/DiMaAddButton"
import { EntityRow, EntityTab } from "../../../../partials/EntityTab"

export function UserApplicationPermissionProperties({
  userApplicationPermission,
  reloadUserApplicationPermission,
  user,
}: {
  readonly userApplicationPermission: ApplicationPermissionUserGet
  readonly reloadUserApplicationPermission: () => void
  readonly user: UserInfo
}) {
  const { requestDelete, DeleteConfirmDialog } = useDeleteConfirm()

  const canDelete = hasUserAccessToApplicationFeature(
    user.authorizations.applications,
    userApplicationPermission.application.id,
    DimaFeatureShortNameEnum.RM,
    AccessLevel.Write
  )

  const onDelete = useCallback(
    (row: { name: string | null; value: string | null }) => {
      requestDelete(() => {
        digitalManagerApi
          .delete(
            `/applications/${userApplicationPermission.application.id}/properties/${row.name}/values/${row.value}`,
            {
              params: {
                userId: userApplicationPermission.user.id,
                permissionId: userApplicationPermission.permission.id,
              },
            }
          )
          .then((res) => {
            if (res.status === StatusCodes.OK) {
              toast.success("Property deleted")
            }
          })
          .catch(() => toast.error("Cannot delete the property"))
          .finally(() => reloadUserApplicationPermission())
      })
    },
    [
      reloadUserApplicationPermission,
      requestDelete,
      userApplicationPermission.application.id,
      userApplicationPermission.permission.id,
      userApplicationPermission.user.id,
    ]
  )

  const cols = [
    { field: "name", headerName: "Name" },
    { field: "label", headerName: "Value" },
  ]

  const rows = userApplicationPermission.properties.map(
    (property: ApplicationPropertyDetail, index: number) => ({
      id: index.toString(),
      name: property.name,
      value: property.value,
      label: property.label,
    })
  )

  const toolbar = useMemo(
    () => (
      <DiMaAddButton
        url={`/users/${userApplicationPermission.user.id}/applications/${userApplicationPermission.application.id}/permissions/${userApplicationPermission.permission.id}/properties/create`}
        feature={DimaFeatureShortNameEnum.RM}
        applicationId={userApplicationPermission.application.id}
      />
    ),
    [userApplicationPermission]
  )

  const handleActionMenuClick = useCallback(
    (item: { title: string; iconName?: string }, row: EntityRow) => {
      const r = row as unknown as Parameters<typeof onDelete>[0]
      if (item.title === "Delete") {
        onDelete(r)
      }
    },
    [onDelete]
  )

  return (
    <>
      {DeleteConfirmDialog}
      <EntityTab
      rows={rows}
      cols={cols}
      isLoading={false}
      toolbar={toolbar}
      actionItems={
        canDelete ? [{ title: "Delete", iconName: "icon_abb_dias icon-abb_delete" }] : []
      }
      onActionMenuClick={handleActionMenuClick}
    />
    </>
  )
}
